/**
 * NOT CHECKED
 */
import React, { useState, useRef, useEffect } from 'react';

import { SVGIcon, SvgIconType } from '@uikit/old/Icon/Icon';
import Input from '@uikit/Form/Input/Input';

import './ActionsTooltipContent.scss';

export type ConfigType = {
  title: string | React.ReactNode;
  icon: SvgIconType;
  handler: (itemId?: number | string) => void;
  iconSize?: number;
  isConfirm?: boolean;
  isDisable?: boolean;
  /** data-testid on this menu item (for ui-automation-tests) */
  testId?: string;
};

type ActionsTooltipContentPropsType = {
  itemId?: string | number;
  onRemove?: () => void;
  onRename?: (string) => void;
  onHideTooltip?: () => void;
  itemTitle?: string;
  config?: ConfigType[];
  isSingleStepRemove?: boolean;
  removeActionTitle?: string;
};

function ActionsTooltipContentItem({
  icon,
  iconSize,
  title,
  handler,
  isConfirm,
  testId,
}: ConfigType) {
  const [confirm, setConfirm] = useState(!isConfirm);
  const [fullTitle, setFullTitle] = useState(title);

  useEffect(() => {
    setConfirm(!isConfirm);
  }, [isConfirm]);

  const handleClick = (event) => {
    if (confirm) {
      handler(event);
    } else {
      setFullTitle('Are you sure?');
      setConfirm(true);
    }
  };

  return (
    <div
      className="actions-tooltip-content__item"
      onClick={handleClick}
      tabIndex={0}
      role="button"
      data-testid={testId}
    >
      <span className="actions-tooltip-content__item-icon">
        <SVGIcon color="#fff" icon={icon} size={iconSize} />
      </span>
      {fullTitle}
    </div>
  );
}

function ActionsTooltipContent({
  config = [],
  itemId,
  onRemove,
  onRename,
  itemTitle,
  onHideTooltip,
  isSingleStepRemove,
  removeActionTitle = 'Remove',
}: ActionsTooltipContentPropsType): JSX.Element {
  const [wasRemoveClicked, changeWasRemoveClicked] = useState(false);
  const [wasRenameClicked, changeWasRenameClicked] = useState(false);
  const [newName, changeNewName] = useState('');
  const inputRef = useRef();

  const handleRemoveClick = (e) => {
    e.stopPropagation();

    if (isSingleStepRemove) {
      onRemove();
      if (onHideTooltip) {
        onHideTooltip();
      }
      return;
    }

    if (wasRemoveClicked) {
      onRemove();
      if (onHideTooltip) {
        onHideTooltip();
      }
    } else {
      changeWasRemoveClicked(true);
    }
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();

    if (!wasRenameClicked) {
      changeWasRenameClicked(true);
    }
  };

  const handleKeyDownOnInput = (e) => {
    if (e.keyCode === 13) {
      onRename(newName);
    }
  };

  const createActionHandler = (handler) => () => {
    handler(itemId);
    if (onHideTooltip) {
      onHideTooltip();
    }
  };

  useEffect(() => {
    if (inputRef.current && wasRenameClicked) {
      // @ts-ignore
      inputRef.current.focus();
    }
  }, [inputRef, inputRef.current, wasRenameClicked]);

  useEffect(() => {
    changeNewName(itemTitle);
  }, [itemTitle]);

  const renderRenameAction = () => {
    if (wasRenameClicked) {
      return (
        <div className="actions-tooltip-content__item">
          <Input
            isTransparent
            isInline
            inputRef={inputRef}
            value={newName}
            onChange={({ target: { value } }) => changeNewName(value)}
            onKeyDown={handleKeyDownOnInput}
          />
        </div>
      );
    }

    {/* TODO: should be removed when "onRename" will be replaced everywhere */}
    return (
      <ActionsTooltipContentItem
        isConfirm={false}
        icon="renameAction"
        title="Rename"
        handler={handleRenameClick}
      />
    );
  };

  return (
    <div
      className="actions-tooltip-content"
      onClick={(e) => e.stopPropagation()}
      tabIndex={0}
      role="button"
    >
      {config.map(({ icon, iconSize, title, handler, isDisable, isConfirm, testId }, index) =>
        isDisable ? null : (
          <ActionsTooltipContentItem
            key={typeof title === 'string' ? title : index}
            icon={icon}
            title={title}
            handler={createActionHandler(handler)}
            iconSize={iconSize}
            isConfirm={isConfirm}
            testId={testId}
          />
        )
      )}
      {/* TODO: should be removed when "onRemove" will be replaced everywhere */}
      {onRemove ? (
        <ActionsTooltipContentItem
          icon="trashWhite"
          isConfirm={false}
          title={wasRemoveClicked ? 'Are you sure?' : removeActionTitle}
          handler={handleRemoveClick}
        />
      ) : null}
      {onRename ? renderRenameAction() : null}
    </div>
  );
}

export default ActionsTooltipContent;
